import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { AssetTypes } from "@zapier/utils";
import { useCallback } from "react";
import { trpc } from "../trpc";

import { Zap } from "../zap/useSearchZaps";
import { SearchZaps } from "../zap/useQuerySearchZaps";

interface Input {
  projectId?: string;
  nodeId?: string | number | null;
}

export interface AddAssetInput {
  assetId: string;
  title?: string;
  type: AssetTypes;
}

export const useAddProjectAsset = ({ projectId, nodeId }: Input) => {
  const queryClient = useQueryClient();

  const assetListKey = getQueryKey(trpc.asset.list, { projectId }, "any");
  const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
  const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");
  const searchProjectZapsKey = getQueryKey(trpc.zap.list, undefined, "any");
  const projectZapsKey = getQueryKey(
    trpc.asset.listByType,
    { assetType: "zap" },
    "any",
  );
  const getZapRunCountsKey = getQueryKey(
    trpc.zapHistory.getZapRunCounts,
    undefined,
    "any",
  );
  const utils = trpc.useUtils();

  const mutation = trpc.asset.add.useMutation({
    onMutate: ({ assetId, projectId: destinationProjectId }) => {
      // If a nodeId is passed in (eg. Zap), update the projectId for the asset in the cache
      if (nodeId) {
        // Update Zap under searchZaps key (search results view)
        const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");
        queryClient.cancelQueries({ queryKey: searchKey }); // cancel outgoing refetches
        queryClient.setQueriesData(
          {
            queryKey: searchKey,
            type: "active",
          },
          (old: SearchZaps | undefined) => {
            // Update projectId for each Zap in the cache results array
            if (
              old?.results?.some((zap) => String(zap.id) === String(nodeId)) ||
              old?.results?.some((zap) => String(zap.id) === assetId)
            ) {
              return {
                ...old,
                results: old.results.map((zap) => {
                  if (
                    String(zap.id) === String(nodeId) ||
                    String(zap.id) === assetId
                  ) {
                    return {
                      ...zap,
                      projectId: destinationProjectId,
                    };
                  }
                  return zap;
                }),
              };
            }

            return old;
          },
        );

        // Update Zap under storageZaps key (search results view)
        queryClient.cancelQueries({ queryKey: storageKey });
        queryClient.setQueriesData(
          {
            queryKey: storageKey,
            type: "active",
          },
          (old: Record<string, Zap> | undefined) => {
            const oldHashToBeUpdated = { ...old };

            // Update projectId for each Zap in the cache hash
            if (oldHashToBeUpdated?.[nodeId]) {
              oldHashToBeUpdated[nodeId] = {
                ...oldHashToBeUpdated[nodeId],
                projectId: destinationProjectId,
              };
            }

            return oldHashToBeUpdated;
          },
        );
      }
    },
    onSuccess: async () => {
      // Refetch the asset list to update the UI
      await utils.asset.list.refetch();
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assetListKey });
      queryClient.invalidateQueries({ queryKey: storageKey });
      queryClient.invalidateQueries({ queryKey: searchKey });
      queryClient.invalidateQueries({ queryKey: searchProjectZapsKey });
      queryClient.invalidateQueries({ queryKey: projectZapsKey });
      queryClient.invalidateQueries({ queryKey: getZapRunCountsKey });
    },
  });

  const onAddAsset = useCallback(
    ({ assetId, type, title }: AddAssetInput) => {
      if (projectId) {
        mutation.mutate({
          projectId,
          assetId,
          type,
          title,
        });
      }
    },
    [mutation, projectId],
  );

  return {
    onAddAsset,
    addProjectAsset: mutation.mutate,
  };
};
